import { tx } from './i18n/t';

// Kleiner Wrapper um fetch: JSON rein/raus, Fehlermeldungen vom Server als ApiError.
// data: die ganze Fehlerantwort des Servers (z. B. details bei 402, wenn der Tarif etwas nicht enthält)
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly data?: Record<string, unknown>,
  ) {
    super(message);
  }
}

// 402 (Billtoro): Der Tarif der Firma enthält das nicht oder eine Grenze ist erreicht; 503 „bald“: im Tarif, aber noch
// nicht verfügbar. Die Antwort geht als Ereignis „nt:tarif“ an das Fenster (billtoro/UpgradeDialog.tsx bietet dann den
// passenden Tarif an); der Aufrufer bekommt trotzdem seinen ApiError. Auch für eigene fetch-Aufrufe
// (rechnung/StapelDaten.ts).
export function tarifFehlerMelden(status: number, data: Record<string, unknown>) {
  if (status === 402 || (status === 503 && data.code === 'bald')) {
    window.dispatchEvent(new CustomEvent('nt:tarif', { detail: data }));
  }
}

async function request<T>(method: string, url: string, body?: unknown, blob = false): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)['content-type'] = 'application/json';
  }
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch {
    throw new ApiError(0, 'Keine Verbindung. Bitte prüfen Sie Ihr Internet und versuchen Sie es noch einmal.');
  }
  if (res.status === 204) return undefined as T;
  if (blob && res.ok) return (await res.blob()) as T;
  const data = (await res.json().catch(() => ({}))) as { error?: string; code?: string } & Record<string, unknown>;
  if (!res.ok) {
    // Sitzung vorbei; „idle“: automatisch abgemeldet (die Anmeldeseite sagt dann warum)
    if (res.status === 401 && !url.startsWith('/api/auth/')) {
      window.dispatchEvent(new CustomEvent('nt:logged-out', { detail: data.code }));
    }
    // 413 ohne Text der App: Der Proxy davor (Caddy) hat die Anfrage schon wegen ihrer Größe abgelehnt.
    if (res.status === 413 && !data.error) throw new ApiError(413, 'Die Datei ist zu groß.', 'too_large');
    tarifFehlerMelden(res.status, data);
    throw new ApiError(res.status, data.error ?? 'Da ist etwas schiefgelaufen.', data.code, data);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body ?? {}),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body ?? {}),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body ?? {}),
  // Antwort als Datei, z. B. die PDF-Vorschau einer Rechnung (Fehler kommen wie sonst als ApiError)
  blob: (url: string, body?: unknown) => request<Blob>('POST', url, body ?? {}, true),
  // Datei laden, z. B. zum Teilen (components/ShareFile.tsx)
  file: (url: string) => request<Blob>('GET', url, undefined, true),
  del: <T>(url: string) => request<T>('DELETE', url),
};

// Meldung für die Anzeige, in der gewählten Sprache (die Texte vom Server sind Deutsch)
export function errorText(err: unknown): string {
  return tx(err instanceof Error ? err.message : 'Da ist etwas schiefgelaufen.');
}
